import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { completeEventRegistration } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/completeEventRegistrationApi";
import type { CompleteEventRegistrationRequest } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/types";
import errorCodeToMessage from "@/modules/eventRegistration/features/EventRegistration/utils/errorMappings";
import { generateInvoiceNumber } from "@/modules/eventRegistration/features/EventRegistration/utils/generateInvoiceNumber";
import { formatCardNumber } from "@/modules/eventRegistration/features/EventRegistration/utils/formatCardNumber";

interface SecureData {
  authData: {
    clientKey: string;
    apiLoginID: string;
  };
  cardData: {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
    fullName: string;
  };
}

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: SecureData,
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
  }
}

interface AcceptResponse {
  messages: {
    resultCode: string;
    message: Array<{
      code: string;
      text: string;
    }>;
  };
  opaqueData: {
    dataDescriptor: string;
    dataValue: string;
  };
}

interface ApiError {
  response?: {
    data?: {
      errorCode?: string;
      message?: string;
    };
  };
}

interface PaymentFormStepProps {
  total: number;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
  handlePaymentSuccess: (eventCheckoutUuid: string) => void;
  buildRequestPayload: (
    dataDescriptor: string,
    dataValue: string,
    amount: number,
    invoiceNumber: string,
    cardExpiration?: string,
  ) => CompleteEventRegistrationRequest;
}

function PaymentFormStep({
  total = 0,
  isProcessing = false,
  setIsProcessing,
  handlePaymentSuccess,
  buildRequestPayload,
}: PaymentFormStepProps) {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(generateInvoiceNumber());
  const [errorMessage, setErrorMessage] = useState("");

  const apiLoginID = process.env.REACT_APP_AUTHNET_API_LOGIN_ID || "";
  const clientKey = process.env.REACT_APP_AUTHNET_CLIENT_KEY || "";

  const regenerateInvoiceNumber = () => {
    setInvoiceNumber(generateInvoiceNumber());
  };

  const handleAcceptResponse = async (response: AcceptResponse) => {
    if (response.messages.resultCode === "Error") {
      if (
        Array.isArray(response.messages.message) &&
        response.messages.message.length
      ) {
        const userMessages = response.messages.message.map((msg) => {
          return errorCodeToMessage[msg.code] || msg.text;
        });
        setErrorMessage(userMessages.join("\n"));
      }
      setIsProcessing(false);
      return;
    }
    setErrorMessage("");
    const { dataDescriptor, dataValue } = response.opaqueData;
    try {
      const normalizedMonth = expiryMonth.padStart(2, "0");
      const normalizedYear =
        expiryYear.length === 2 ? `20${expiryYear}` : expiryYear;
      const cardExpiration = `${normalizedYear}-${normalizedMonth}`;
      const payload = buildRequestPayload(
        dataDescriptor,
        dataValue,
        total,
        invoiceNumber,
        cardExpiration,
      );

      const result = await completeEventRegistration(payload);

      setIsProcessing(false);

      if (result.data.success && result.data.eventCheckoutUuid) {
        handlePaymentSuccess(result.data.eventCheckoutUuid);
      }
    } catch (error: unknown) {
      setIsProcessing(false);
      const apiError = error as ApiError;
      const errorMsg = apiError.response?.data?.message;
      setErrorMessage(
        errorMsg ||
          "An error occurred while processing your payment. Please try again.",
      );
      regenerateInvoiceNumber();
    }
  };

  const buildSecureData = (): SecureData => ({
    authData: { clientKey, apiLoginID },
    cardData: {
      cardNumber: cardNumber.replace(/\s+/g, ""),
      month: expiryMonth,
      year: expiryYear,
      cardCode,
      fullName: cardholderName,
    },
  });

  const handlePayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessing(true);
    setErrorMessage("");
    regenerateInvoiceNumber();

    // Tokenize card immediately — availability is checked server-side inside the lock
    const secureData = buildSecureData();

    if (window.Accept && typeof window.Accept.dispatchData === "function") {
      window.Accept.dispatchData(secureData, handleAcceptResponse);
    } else {
      setIsProcessing(false);
      setErrorMessage(
        "Payment functionality is currently unavailable. Please contact support.",
      );
    }
  };

  return (
    <>
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
          Payment Information
        </h3>
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
            {errorMessage}
          </div>
        )}
        <form className="space-y-4" id="payment-form" onSubmit={handlePayment}>
          <div className="space-y-2">
            <Label htmlFor="cardholderName">Cardholder Name</Label>
            <Input
              disabled={isProcessing}
              id="cardholderName"
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="John Doe"
              required
              type="text"
              value={cardholderName}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cardNumber">Card Number</Label>
            <Input
              disabled={isProcessing}
              id="cardNumber"
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="4111 1111 1111 1111"
              required
              type="text"
              value={cardNumber}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expiryMonth">Month</Label>
              <Input
                disabled={isProcessing}
                id="expiryMonth"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 2);
                  setExpiryMonth(value);
                }}
                placeholder="MM"
                required
                type="text"
                value={expiryMonth}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiryYear">Year</Label>
              <Input
                disabled={isProcessing}
                id="expiryYear"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 2);
                  setExpiryYear(value);
                }}
                placeholder="YY"
                required
                type="text"
                value={expiryYear}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardCode">CVV</Label>
              <Input
                disabled={isProcessing}
                id="cardCode"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 4);
                  setCardCode(value);
                }}
                placeholder="123"
                required
                type="text"
                value={cardCode}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default PaymentFormStep;
